import { EventEmitter, Injectable, Output, untracked } from "@angular/core";
import { GlobalTranslationVar } from "@commonClass/GlobalTranslationVar";
import { Language } from "@commonEnums/language";
import { isNullish } from "@commonHelpers/math-utils";
import { CcmModel } from "@commonInterfaces/CcmModel";
import { TranslateService } from "@ngx-translate/core";
import { AccountLanguage } from "@interfaces/HttpClient/AccountApiPublicModels";
import { CatalogLanguage } from "@interfaces/HttpClient/CatalogApiCatalogViewModels";
import { firstValueFrom } from "rxjs";
import { GlobalVarService } from "./global-var.service";

declare const CCM: CcmModel;
@Injectable({
	providedIn: "root"
})
export class TranslationService {
	// Merkt sich nur, welche Sprachen bereits im ngx-translate-Store liegen. Früher lag hier
	// eine Map mit den Übersetzungen selbst; die konnte nie gefüllt werden, weil der
	// MessageFormat-Compiler jedes Blatt in eine Funktion übersetzt und die Befüllung an
	// `typeof <marker> === "string"` hängt. Die Werte werden deshalb über
	// TranslateService.instant gelesen, das kompilierte Funktionen selbst auflöst.
	private static readonly loadedLanguages = new Set<string>();
	private static currentLoadingLanguage?: Language;

	constructor(private translateService: TranslateService, private globalVarService: GlobalVarService
	) { }

	/*     formatMessage = formatMessage; */

	@Output() languageChanged = new EventEmitter();

	// ngx-translate 18 hält den Übersetzungsspeicher in Signals; `use` und `reloadLang`
	// schreiben sie synchron beim Aufruf. `loadLanguage` wird aus der unreinen Pipe
	// `languageTranslation` heraus aufgerufen, also mitten im Rendern - ein Signal-Schreibzugriff
	// mit aktivem Consumer wirft dort NG0600 (reproduzierbar beim Öffnen des Benutzermenüs).
	// `untracked` hebt den aktiven Consumer für den Aufruf auf und macht den Schreibzugriff
	// wieder zulässig, ohne das Ladeverhalten zu verändern.
	private async switchLanguage(language: Language) {
		TranslationService.currentLoadingLanguage = language;
		await untracked(() => firstValueFrom(this.translateService.use(language)));
		TranslationService.currentLoadingLanguage = undefined;
		TranslationService.loadedLanguages.add(language);
		GlobalTranslationVar.SELECTED_LANGUAGE = language;
		this.languageChanged.emit(language);
		this.setCCM19Language(language);
		document.querySelector('html')?.setAttribute('lang', GlobalTranslationVar.SELECTED_LANGUAGE);

	}

	public setCCM19Language(language: Language) {
		try {
			if (!isNullish(CCM)) {
				this.switchCcm19Language(language);
			} else {
				const switchLanguageFunction = () => {
					this.switchCcm19Language(language);
					window.removeEventListener('ccm19WidgetLoaded', switchLanguageFunction);
				}
				window.addEventListener('ccm19WidgetLoaded', switchLanguageFunction);
			}
		} catch {

		}
	}

	private switchCcm19Language(language: Language) {
		const ccmWidgetElement = document.getElementById('ccm-widget');
		const currentLanguage = ccmWidgetElement.getAttribute('lang')
		if (language === Language.DE) {
			if (!currentLanguage.startsWith(Language.DE)) {
				CCM?.switchLocale('de_DE')
			}

		} else {
			if (!currentLanguage.startsWith(Language.EN)) {
				CCM?.switchLocale('en_US')
			}

		}
	}

	async switchCatalogLanguage(catalogLanguage: CatalogLanguage) {
		if (catalogLanguage === CatalogLanguage.German) {
			await this.switchLanguage(Language.DE);
		} else {
			await this.switchLanguage(Language.EN)
		}

	}

	async switchAdminLanguage(accountLanguage: AccountLanguage) {
		switch (accountLanguage) {
			case AccountLanguage.English: { await this.switchLanguage(Language.EN); break }
			case AccountLanguage.German: { await this.switchLanguage(Language.DE); break }
		}
	}

	private getLanguageKey(accountLanguage: AccountLanguage) {
		switch (accountLanguage) {
			case AccountLanguage.English: return Language.EN;
			case AccountLanguage.German: return Language.DE;
		}
	}

	getLanguage() {
		let lang = GlobalTranslationVar.SELECTED_LANGUAGE as string;

		if (!lang) {
			lang = Language.DE;
		}

		return lang.substring(0, 2).toLowerCase();
	}

	getByKey(key: string, params?) {
		if (params) {
			return this.translateService.instant(key, params);
		}
		return this.translateService.instant(key);
	}

	getByKeyAndLanguage(key: string, accountLanguage: AccountLanguage): string {
		// getLanguageKey deckt nur die bekannten Konten-Sprachen ab; derselbe Rückfall wie in
		// getLanguage(), damit ein unbekannter Wert nicht in undefined läuft.
		const langKey = this.getLanguageKey(accountLanguage) ?? Language.DE;
		if (!TranslationService.loadedLanguages.has(langKey)) {
			return '';
		}
		// instant() mit explizitem Sprachkürzel: es liest den Store und lässt die vom
		// MessageFormat-Compiler erzeugten Funktionen über den Parser auflösen. Ein direkter
		// Zugriff auf die Store-Werte würde die Funktion selbst liefern, nicht ihren Text.
		return this.translateService.instant(key, undefined, langKey);
	}

	async loadLanguage(accountLanguage: AccountLanguage) {
		if (isNullish(accountLanguage)) {
			return "";
		}
		const translationKey = this.getLanguageKey(accountLanguage) ?? Language.DE;
		if (!TranslationService.loadedLanguages.has(translationKey) && TranslationService.currentLoadingLanguage !== translationKey) {
			TranslationService.currentLoadingLanguage = translationKey;
			try {
				await untracked(() => firstValueFrom(this.translateService.reloadLang(translationKey)));
				TranslationService.loadedLanguages.add(translationKey);
			} finally {
				// Muss auch im Fehlerfall zurückgesetzt werden, sonst bleibt die Sprache
				// dauerhaft als "wird geladen" markiert und wird nie erneut versucht.
				TranslationService.currentLoadingLanguage = undefined;
			}
		}
		return true;
	}

	async setDefaultLanguage() {
		await this.switchLanguage(Language.DE);
	}

	async initTranslation() {
		// bei user soll auch beim initalen aufruf die Sprache gesetzt werden. Da keine Id oder Unterteilung vorhanden sein kann muss in dem Fall der Slash weggelassen werden  
		const translationUrls = [
			"/administration/companies/",
			"/administration/catalog/",
			"/administration/user",
			"/preview/"]

		const tmpTranslationUrls = [
			"/watchlist/",
			"/search",
			"/international-catalogs",
			"/category",
			"/maintenance",
			"/a-z"]

		const urlLanguage = new URLSearchParams(window.location.search).get("language");
		const hasTranslationUrls = translationUrls.some(e => window.location.pathname.startsWith(e));
		// Initialabfrage nach Urlparameter
		if (hasTranslationUrls && !isNullish(urlLanguage)) {

			switch (urlLanguage) {
				case 'German': { await this.switchLanguage(Language.DE); break; }
				case 'English': { await this.switchLanguage(Language.EN); break; }
				default: { await this.setDefaultLanguage(); }
			}
			// Initialabfrage nach Benutzersprache 
		} else if (hasTranslationUrls && !isNullish(this.globalVarService.getLanguage())) {
			await this.switchAdminLanguage(this.globalVarService.getLanguage());
			// Initialabfrage nach derzeit eingestellter Spache über den Header 
		} else if (!isNullish(this.globalVarService.getTmpLanguage()) && (window.location.pathname === "/" || tmpTranslationUrls.some(e => window.location.pathname.startsWith(e)))) {
			await this.switchAdminLanguage(this.globalVarService.getTmpLanguage());
		} else {
			await this.setDefaultLanguage();
		}
	}
}
